import { TenantDto, TenantRoleDto, UserDto } from '@admin/app/api'
import {
  Card,
  CardContent,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useTranslate } from '@tolgee/react'
import { useMinimalsTheme } from '@tom-ui/ui'
import { FormField, IFormStore } from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import { UserFormProfile } from '../../models/user-form.profile'
import { RolesAutoComplete } from '../molecules/RolesAutoComplete'
import { TenantsAutoComplete } from '../molecules/TenantsAutoComplete'

interface Props {
  user?: UserDto
  roles: TenantRoleDto[]
  userRoles: TenantRoleDto[]
  tenants: TenantDto[]
  formStore: IFormStore<UserFormProfile>
  canManageTenant?: boolean
}

export const UserForm = observer(
  ({ user, roles, userRoles, tenants, formStore, canManageTenant }: Props) => {
    const { t } = useTranslate()
    const theme = useMinimalsTheme()
    const [isSuperUser, setIsSuperUser] = useState(user?.isSuperuser)

    return (
      <Card>
        <CardContent>
          <Stack component='form' noValidate autoComplete='off' gap={theme.customSpacing.l}>
            <Stack flexDirection='row' justifyContent='space-between' alignItems='center'>
              <Typography variant='subtitle1'>{t('userDetails', 'User details')}</Typography>
            </Stack>

            <FormField
              formStore={formStore}
              name={'email'}
              initialValue={user?.email}
              validate={value => {
                if (!value) return t('fieldRequired', 'This field is required')
              }}
            >
              {store => (
                <Stack gap={theme.customSpacing.s}>
                  <TextField
                    {...store.props}
                    fullWidth
                    label={t('email', 'Email')}
                    required
                    name='email'
                    error={!!store.error}
                  />
                  {!!store.error && (
                    <Typography variant='caption' color='error'>
                      {store.error}
                    </Typography>
                  )}
                </Stack>
              )}
            </FormField>

            <FormField
              formStore={formStore}
              name={'firstName'}
              initialValue={user?.given_name}
              validate={value => {
                if (!value) return t('fieldRequired', 'This field is required')
              }}
            >
              {store => (
                <Stack gap={theme.customSpacing.s}>
                  <TextField
                    {...store.props}
                    fullWidth
                    label={t('firstName', 'First name')}
                    required
                    name='firstName'
                  />
                  {!!store.error && (
                    <Typography variant='caption' color='error'>
                      {store.error}
                    </Typography>
                  )}
                </Stack>
              )}
            </FormField>

            <FormField
              formStore={formStore}
              name={'lastName'}
              initialValue={user?.family_name}
              validate={value => {
                if (!value) return t('fieldRequired', 'This field is required')
              }}
            >
              {store => (
                <Stack gap={theme.customSpacing.s}>
                  <TextField
                    {...store.props}
                    fullWidth
                    label={t('lastName', 'Last name')}
                    required
                    name='lastName'
                  />
                  {!!store.error && (
                    <Typography variant='caption' color='error'>
                      {store.error}
                    </Typography>
                  )}
                </Stack>
              )}
            </FormField>

            {canManageTenant && (
              <>
                <FormField
                  formStore={formStore}
                  name={'tenantId'}
                  initialValue={user?.tenantId}
                  validate={value => {
                    if (!value) return t('fieldRequired', 'This field is required')
                  }}
                >
                  {store => (
                    <FormControl fullWidth error={!!store.error}>
                      <TenantsAutoComplete
                        options={tenants}
                        defaultValue={tenants.find(t => t.id === store.value) ?? undefined}
                        onChange={tenant => store.setValue(tenant?.id)}
                        error={!!store.error}
                        getOptionLabel={option => option.name}
                        required
                      />
                      {!!store.error && <FormHelperText error>{store.error}</FormHelperText>}
                    </FormControl>
                  )}
                </FormField>

                <FormField
                  formStore={formStore}
                  name={'isSuperUser'}
                  initialValue={user?.isSuperuser}
                >
                  {store => (
                    <FormControlLabel
                      value={store.value ?? false}
                      label={t('isSuperUser', 'Is super user')}
                      sx={{ width: 'fit-content' }}
                      control={
                        <Checkbox
                          onChange={(_, checked) => {
                            store.setValue(checked)
                            setIsSuperUser(checked)
                          }}
                          checked={store.value}
                        />
                      }
                    />
                  )}
                </FormField>
              </>
            )}

            {!isSuperUser && (
              <FormField<UserFormProfile, TenantRoleDto[]>
                formStore={formStore}
                name={'roles'}
                initialValue={userRoles ?? []}
                validate={value =>
                  !value.length ? t('fieldIsRequired', 'Field is required') : undefined
                }
              >
                {store => (
                  <FormControl fullWidth>
                    <Stack gap={theme.customSpacing.s}>
                      <RolesAutoComplete
                        multiple
                        defaultValue={userRoles ?? []}
                        error={!!store.error}
                        onChange={store.setValue}
                        options={roles}
                        required
                        getOptionLabel={option => option.name}
                      />
                      {!!store.error && (
                        <Typography variant='caption' color='error'>
                          {store.error}
                        </Typography>
                      )}
                    </Stack>
                  </FormControl>
                )}
              </FormField>
            )}
          </Stack>
        </CardContent>
      </Card>
    )
  },
)
